import classNames from 'classnames'
import { useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import type {
  BigNumbersItemData,
  BigNumbersProps,
  BigNumbersTheme,
} from 'src/types/bigNumbers2'

import BigNumberItem from './BigNumberItem'

const MAX_ITEMS = 4

const getGridColumnsClass = (itemsLength: number) => {
  if (itemsLength <= 2) {
    return 'md:grid-cols-[repeat(2,minmax(0,340px))]'
  }

  if (itemsLength === 3) {
    return 'md:grid-cols-[repeat(3,minmax(0,340px))]'
  }

  return 'md:grid-cols-[repeat(4,minmax(0,340px))]'
}

export default function BigNumbers20({
  theme = 'light',
  items = [],
}: BigNumbersProps) {
  const normalizedTheme: BigNumbersTheme = theme === 'dark' ? 'dark' : 'light'

  const parsedItems = useMemo(() => {
    return items
      .filter(
        (item?: BigNumbersItemData) =>
          item?.bigNumber?.trim() && item?.subtext?.trim()
      )
      .slice(0, MAX_ITEMS)
  }, [items])

  const { ref, inView } = useInView({
    threshold: 0.35,
    triggerOnce: true,
  })

  if (parsedItems.length < 2) {
    return null
  }

  return (
    <section
      ref={ref}
      data-testid="big-numbers-2-0"
      className={classNames(
        'w-full',
        normalizedTheme === 'dark'
          ? 'bg-darkThemeComponents border-y border-white/[12%]'
          : 'bg-white'
      )}
    >
      <div className="mx-auto py-9 w-full max-w-[1550px]">
        <div
          className={classNames(
            'grid grid-cols-[repeat(2,1fr)] gap-y-10 justify-center justify-items-center text-center',
            getGridColumnsClass(parsedItems.length)
          )}
        >
          {parsedItems.map((item, index) => {
            return (
              <div
                key={`${item.bigNumber}-${index}`}
                className={classNames(
                  'w-full h-full',
                  index % 2 === 1 ? 'border-l' : 'border-l-0',
                  index > 0 ? 'md:border-l' : 'md:border-l-0',
                  normalizedTheme === 'dark'
                    ? 'border-white/[12%]'
                    : 'border-gray'
                )}
              >
                <BigNumberItem
                  bigNumber={item.bigNumber}
                  subtext={item.subtext}
                  theme={normalizedTheme}
                  shouldStartCount={inView}
                />
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
